import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { CircleCheck } from 'lucide-react';
import { ErrorBanner } from '@/components/form';
import { AuthCard } from '@/components/marketing/auth-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { auth } from '@/lib/auth';
import { useT } from '@/lib/i18n';

export const Route = createFileRoute('/signup')({ component: SignupPage });

const PERKS = ['perk1', 'perk2', 'perk3'] as const;

function SignupPage() {
  const navigate = useNavigate();
  const t = useT();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!auth.enabled) {
      void navigate({ to: '/dashboard' });
      return;
    }
    setBusy(true);
    setFailed(false);
    void auth.signIn('/dashboard', { email: email.trim(), signUp: true }).catch(() => {
      // the identity provider is unreachable: say so instead of re-enabling the button silently
      setBusy(false);
      setFailed(true);
    });
  };

  return (
    <AuthCard
      title={t('publicPages.signup.title')}
      subtitle={t('publicPages.signup.subtitle')}
      footer={
        <span className="text-brand-slate">
          {t('publicPages.signup.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            {t('publicPages.header.logIn')}
          </Link>
        </span>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <ErrorBanner>{failed ? t('publicPages.signup.failed') : null}</ErrorBanner>
        <label className="block">
          <span className="text-xs font-semibold text-brand-slate">
            {t('publicPages.signup.workEmail')}
          </span>
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            required={auth.enabled}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 h-10 bg-white"
            data-testid="signup-email"
          />
        </label>
        <ul className="space-y-2 rounded-md bg-[#f7f8f9] p-4">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-xs text-brand-ink">
              <CircleCheck className="mt-px size-3.5 shrink-0 text-success" />
              {t(`publicPages.signup.${p}`)}
            </li>
          ))}
        </ul>
        <p className="text-xs leading-relaxed text-brand-slate">{t('publicPages.signup.terms')}</p>
        <Button type="submit" className="h-10 w-full font-semibold" disabled={busy}>
          {busy ? t('publicPages.signup.opening') : t('publicPages.header.signUp')}
        </Button>
      </form>
    </AuthCard>
  );
}
